/**
 * Admin Portal → Partners → a partner → Settings → Co-branded page (partner launch UC2,
 * workstream S5). docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md §1 UC2, §2 E9/E10.
 *
 * What the partner's co-branded hero on coachcedar.com shows — the headline, the message and
 * their logo — and, prominently, the **Partner page link** the partner sends to their people:
 * `https://coachcedar.com/go/<tag>?d=with&p=<code>`, with Copy and a QR code (drawn by the
 * website code card's helpers, so it can only ever encode a /go link). The bare
 * coachcedar.com/with/<code> address records no click — the card says so.
 *
 * The logo is UPLOADED to our own storage (plan E10: no third-party image on our pages, and a
 * partner can't swap it); it is never typed as a URL. The server sets `landing_content.logo_url`.
 *
 * Every admin role can read the card and copy the link (copying is a read); saving, uploading and
 * removing need an admin or super admin session.
 *
 * Web-only (the admin SPA).
 */

import React, { useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  LANDING_BODY_MAX,
  LANDING_HEADLINE_MAX,
  checkLogoFile,
  landingContentBody,
  partnerPageLink,
  type PartnerRecord,
  type TagRow,
} from './partnersLogic';
import { checkGoUrl, qrSvg } from './websiteCode';
import { downloadPng, downloadSvg } from './PartnerWebsiteCode';
import { NoticeLine, copyText, errText, hint, input, label, row, useBusy, type Notice } from './PartnersShared';

/** The tags whose Partner page link is worth sharing: not ended (live first). */
export function linkableTags(tags: TagRow[]): TagRow[] {
  const live = tags.filter((t) => t.status !== 'ended');
  return [...live.filter((t) => t.status === 'active'), ...live.filter((t) => t.status !== 'active')];
}

export const PartnerLandingCard: React.FC<{
  partner: PartnerRecord;
  tags: TagRow[];
  writable: boolean;
  onChanged: () => void;
}> = ({ partner, tags, writable, onChanged }) => {
  const saved = partner.landing_content || {};
  const [headline, setHeadline] = useState(saved.headline ?? '');
  const [body, setBody] = useState(saved.body ?? '');
  const [notice, setNotice] = useState<Notice>(null);
  const [copied, setCopied] = useState(false);
  const [busy, run] = useBusy();

  const choices = useMemo(() => linkableTags(tags), [tags]);
  const [linkTagId, setLinkTagId] = useState<string>(choices[0]?.id ?? '');
  const linkTag = choices.find((t) => t.id === linkTagId) ?? choices[0] ?? null;
  const link = linkTag && partner.status !== 'ended' ? partnerPageLink(linkTag.tag, partner.code) : null;
  const linkOk = link ? checkGoUrl(link).ok : false;
  const qrSrc = useMemo(() => {
    if (!link || !linkOk) return null;
    try {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg(link, { size: 160 }))}`;
    } catch {
      return null;
    }
  }, [link, linkOk]);

  const dirty = headline.trim() !== (saved.headline ?? '') || body.trim() !== (saved.body ?? '');

  const save = () =>
    run(async () => {
      const res = landingContentBody({ headline, body, logoUrl: saved.logo_url ?? '' });
      if (res.errors.length) { setNotice({ kind: 'error', text: 'Not saved:', extra: res.errors }); return; }
      try {
        await PartnersAdmin.updatePartner(partner.id, res.body);
        setNotice({ kind: 'ok', text: 'Saved: the co-branded page shows this from now on.' });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const uploadLogo = (file: (Blob & { name: string }) | null) =>
    run(async () => {
      const problem = checkLogoFile(file);
      if (problem || !file) { setNotice({ kind: 'error', text: problem ?? 'Choose a logo file first.' }); return; }
      const form = new FormData();
      form.append('file', file, file.name);
      try {
        await PartnersAdmin.uploadLandingLogo(partner.id, form);
        setNotice({ kind: 'ok', text: 'Logo uploaded: it is on the co-branded page now.' });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  // Removing the logo keeps the SAVED headline and message (never an unsaved edit).
  const removeLogo = () =>
    run(async () => {
      const res = landingContentBody({ headline: saved.headline ?? '', body: saved.body ?? '', logoUrl: '' });
      try {
        await PartnersAdmin.updatePartner(partner.id, res.body);
        setNotice({ kind: 'ok', text: 'Logo removed.' });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const copy = async () => {
    if (!link) return;
    setCopied(await copyText(link));
  };

  const saveQr = (kind: 'svg' | 'png') => {
    if (!link) return;
    const ok = kind === 'svg' ? downloadSvg(link) : downloadPng(link);
    if (!ok) setNotice({ kind: 'error', text: `The browser could not save the ${kind.toUpperCase()} — try the other format, or another browser.` });
  };

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Co-branded page</h4>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {/* ── the Partner page link: what the partner sends ── */}
      <div data-testid="partner-page-link" style={{ padding: 12, borderRadius: 10, border: '1px solid var(--border-strong, #d1d5db)', background: 'var(--surface-subtle, #f9fafb)' }}>
        <b style={{ fontSize: 13 }}>Partner page link</b>
        {!linkTag && (
          <div style={hint}>Make a tag first (Links &amp; tags): the Partner page link is a tag&apos;s link.</div>
        )}
        {linkTag && partner.status === 'ended' && <div style={hint}>This partner has ended: there is no link to share.</div>}
        {link && (
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            {choices.length > 1 && (
              <div>
                <label style={label} htmlFor="lp-link-tag">Tag</label>
                <select id="lp-link-tag" style={input} value={linkTag?.id ?? ''} onChange={(e) => { setLinkTagId(e.target.value); setCopied(false); }}>
                  {choices.map((t) => <option key={t.id} value={t.id}>{t.tag}{t.status !== 'active' ? ` (${t.status})` : ''}</option>)}
                </select>
              </div>
            )}
            <code style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}>{link}</code>
            <AdminButton size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</AdminButton>
          </div>
        )}
        {link && qrSrc && (
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            <img data-testid="partner-page-qr" src={qrSrc} width={120} height={120} alt={`QR code for ${link}`} />
            <AdminButton size="sm" variant="secondary" onClick={() => saveQr('svg')}>Download QR (SVG)</AdminButton>
            <AdminButton size="sm" variant="secondary" onClick={() => saveQr('png')}>Download QR (PNG)</AdminButton>
          </div>
        )}
        {link && linkTag && linkTag.status !== 'active' && (
          <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>This tag is {linkTag.status}: visits through it are not earning while it stays that way.</div>
        )}
        <div style={hint}>
          Share this link, never the bare coachcedar.com/with/{partner.code.toLowerCase()} address: on its own that address
          {' '}records no click, so nothing it brings in is attributed to {partner.display_name}. Scanning the QR code records a real visit.
        </div>
        {!saved.headline && (
          <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
            Save a headline first: until then the page shows nothing of {partner.display_name}, and Links &amp; tags cannot build this link.
          </div>
        )}
      </div>

      {/* ── the content ── */}
      <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
        <div>
          <label style={label} htmlFor="lp-headline">Headline ({headline.trim().length}/{LANDING_HEADLINE_MAX})</label>
          <input id="lp-headline" style={{ ...input, width: '100%' }} disabled={!writable} value={headline} onChange={(e) => setHeadline(e.target.value)} />
        </div>
        <div>
          <label style={label} htmlFor="lp-body">Message ({body.trim().length}/{LANDING_BODY_MAX.toLocaleString('en-CA')})</label>
          <textarea id="lp-body" rows={3} style={{ ...input, width: '100%' }} disabled={!writable} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <div>
          <span style={label}>Logo</span>
          <div style={{ ...row, alignItems: 'center' }}>
            {saved.logo_url
              ? <img src={saved.logo_url} alt={`${partner.display_name} logo`} referrerPolicy="no-referrer" style={{ maxHeight: 48, maxWidth: 200 }} />
              : <span style={hint}>No logo — the page shows the text lockup &quot;{partner.display_name} × Coach Cedar&quot;.</span>}
            {writable && (
              <>
                <label style={{ fontSize: 13 }}>
                  {saved.logo_url ? 'Replace logo: ' : 'Upload logo: '}
                  <input id="lp-logo" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" disabled={busy}
                    onChange={(e) => uploadLogo((e.target.files && e.target.files[0]) ? (e.target.files[0] as Blob & { name: string }) : null)} />
                </label>
                {saved.logo_url && <AdminButton size="sm" variant="ghost" onClick={removeLogo} disabled={busy}>Remove logo</AdminButton>}
              </>
            )}
          </div>
          <div style={hint}>PNG, JPEG, WebP or SVG, up to 512 KB. It is stored by Coach Cedar — use their real logo only once they have agreed.</div>
        </div>
        {writable && (
          <div><AdminButton size="sm" onClick={save} disabled={busy || !dirty}>Save page</AdminButton></div>
        )}
      </div>
    </AdminCard>
  );
};

export default PartnerLandingCard;
